const s =
  "bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse";

const CardShell = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] pt-4 px-4 flex flex-col gap-3 min-w-0 ${className}`}
  >
    <div>
      <div className="flex gap-1 items-center">
        <div className={`h-4 w-4 rounded ${s}`} />
        <div className={`h-4 w-32 rounded ${s}`} />
      </div>
      <div className={`h-3 w-52 rounded mt-1 ${s}`} />
    </div>
    {children}
  </div>
);

const DonutRingShimmer = () => (
  <div className="relative shrink-0 w-[222px] h-[222px]">
    <div
      className={`absolute inset-0 rounded-full ${s}`}
      style={{
        mask: "radial-gradient(circle, transparent 55%, black 56%)",
        WebkitMask: "radial-gradient(circle, transparent 55%, black 56%)",
      }}
    />
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className={`h-5 w-10 rounded ${s}`} />
      <div className={`h-3 w-16 rounded mt-1.5 ${s}`} />
    </div>
  </div>
);

export const MetricShimmer = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex-1 min-w-0 flex flex-row items-center gap-2">
        <div className="flex flex-col w-full gap-2.5 border border-border-gray dark:border-border-gray-dk rounded-[8px] py-2.5 h-[75px] px-3">
          <div className={`h-6 w-14 rounded ${s}`} />
          <div className={`h-3.5 w-20 rounded ${s}`} />
        </div>
        {i === 0 && (
          <div className="h-4 w-px bg-border-gray dark:bg-border-gray-dk" />
        )}
      </div>
    ))}
  </>
);

export const BarChartCardShimmer = () => (
  <CardShell className="gap-1! max-h-[286px]">
    <div className="flex flex-col gap-3 h-[222px] justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className={`h-5 w-24 rounded-md ${s}`} />
          <div
            className={`h-2 rounded-full ${s}`}
            style={{ width: `${65 - i * 8}%` }}
          />
          <div className={`h-3 w-6 rounded ${s}`} />
        </div>
      ))}
    </div>
  </CardShell>
);

export const SLAChartShimmer = () => (
  <CardShell className="gap-1! max-h-[286px]">
    <div className="flex items-center justify-between w-full -mt-1">
      <div className="flex flex-col gap-[18px] shrink-0">
        {[0, 1].map((i) => (
          <div key={i}>
            <div className="flex gap-2.5 items-center">
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${s}`} />
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <div className={`h-5 w-10 rounded ${s}`} />
                  <div className="w-px h-3.5 bg-border-gray dark:bg-border-gray-dk" />
                  <div className={`h-4 w-8 rounded ${s}`} />
                </div>
                <div className={`h-3 w-28 rounded ${s}`} />
              </div>
            </div>
            {i === 0 && (
              <div className="h-px w-full mt-[18px] bg-border-gray dark:bg-border-gray-dk" />
            )}
          </div>
        ))}
      </div>
      <DonutRingShimmer />
    </div>
  </CardShell>
);

export const PriorityChartShimmer = () => (
  <CardShell className="gap-1! max-h-[286px] pb-1.5!">
    <div className="flex items-center justify-between w-full -mt-2">
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={`flex items-center justify-between gap-2 rounded-[8px] px-3 py-2 ${s}`}
          >
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded bg-white/20" />
              <div className="h-3.5 w-12 rounded bg-white/20" />
            </div>
            <div className="h-3 w-16 rounded bg-white/20" />
          </div>
        ))}
      </div>
      <DonutRingShimmer />
    </div>
  </CardShell>
);

export const IssueListShimmer = () => (
  <CardShell className="h-[286px]">
    <div className="flex flex-col gap-2 overflow-hidden flex-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[10px] flex items-center justify-between px-3 py-2 shrink-0"
        >
          <div className="flex flex-col gap-1 min-w-0 mr-3">
            <div
              className={`h-3.5 rounded ${s}`}
              style={{ width: `${70 + i * 4}%` }}
            />
            <div className={`h-3 w-16 rounded ${s}`} />
          </div>
          <div className={`shrink-0 w-8 h-7 rounded-full ${s}`} />
        </div>
      ))}
    </div>
  </CardShell>
);

export const SiteTableShimmer = () => (
  <CardShell className="gap-4! pb-px h-[286px]">
    <div className="rounded-[8px] border border-border-gray dark:border-border-gray-dk overflow-hidden flex-1 min-h-0">
      <div className="bg-table-header dark:bg-table-header-dk h-[34px] flex items-center px-2 gap-4">
        <div className={`h-3 w-20 rounded ${s}`} />
        <div className={`h-3 w-20 rounded ${s}`} />
        <div className={`h-3 w-20 rounded ${s}`} />
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-[34px] flex items-center px-2 gap-4 border-t border-gray-75 dark:border-gray-75-dk"
        >
          <div className={`h-2.5 w-24 rounded ${s}`} />
          <div className={`h-2.5 w-10 rounded ${s}`} />
          <div className={`h-[18px] w-10 rounded ${s}`} />
        </div>
      ))}
    </div>
  </CardShell>
);

export const WideBarChartShimmer = ({
  dualLegend = false,
}: {
  dualLegend?: boolean;
}) => (
  <div className="bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] pt-4 px-4 pb-2 flex flex-col gap-4 min-w-0">
    <div className="flex items-start justify-between">
      <div>
        <div className="flex gap-1 items-center">
          <div className={`h-4.5 w-4.5 rounded ${s}`} />
          <div className={`h-4 w-44 rounded ${s}`} />
        </div>
        <div className={`h-3 w-56 rounded mt-1 ${s}`} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {dualLegend && <div className={`h-[22px] w-[72px] rounded-md ${s}`} />}
        <div className={`h-[22px] w-16 rounded-md ${s}`} />
      </div>
    </div>
    <div className="h-[280px] flex items-end gap-2 pb-8">
      {Array.from({ length: 12 }).map((_, i) =>
        dualLegend ? (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
            <div className="flex items-end gap-0.5 w-full justify-center">
              <div
                className={`w-[14px] rounded-t-md ${s}`}
                style={{
                  height: `${30 + Math.abs(Math.sin(i * 1.2)) * 70}%`,
                }}
              />
              <div
                className={`w-[14px] rounded-t-md ${s}`}
                style={{
                  height: `${20 + Math.abs(Math.cos(i * 1.5)) * 60}%`,
                }}
              />
            </div>
            <div className={`h-2.5 w-6 rounded ${s}`} />
            <div className={`h-2 w-7 rounded ${s}`} />
          </div>
        ) : (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
            <div
              className={`w-full max-w-[31px] rounded-t-md ${s}`}
              style={{
                height: `${30 + Math.abs(Math.sin(i * 1.2)) * 70}%`,
              }}
            />
            <div className={`h-2.5 w-6 rounded ${s}`} />
            <div className={`h-2 w-7 rounded ${s}`} />
          </div>
        )
      )}
    </div>
  </div>
);
